import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { authApi, authStore } from "@/lib/apiClient";
import { adminDashboardApi, adminSystemApi } from "@/lib/commerce";
import { formatMoney } from "@/lib/format";
import { AUTH } from "@/constants/testIds";

export default function AdminHome() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wipeBusy, setWipeBusy] = useState(false);
  const [wipeResult, setWipeResult] = useState(null);
  const [wipeErr, setWipeErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [m, s] = await Promise.all([authApi.me(), adminDashboardApi.summary()]);
        if (!alive) return;
        setMe(m);
        setSummary(s);
      } catch (e) {
        if (!alive) return;
        setError(e.response?.data?.error?.message || "Failed to load.");
      }
    })();
    return () => { alive = false; };
  }, []);

  const logout = async () => {
    await authApi.logout();
    authStore.clear();
    nav("/admin/login", { replace: true });
  };

  const runWipe = async () => {
    setWipeBusy(true);
    setWipeErr("");
    try {
      const res = await adminSystemApi.wipeOrders();
      setWipeResult(res.deleted);
      // refresh dashboard KPIs so they immediately reflect zero
      try { setSummary(await adminDashboardApi.summary()); } catch { /* ignore */ }
    } catch (e) {
      setWipeErr(e.response?.data?.error?.message || "Wipe failed. Try again.");
    } finally {
      setWipeBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-home-root">
      <header className="border-b bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin</div>
            <div className="text-lg font-semibold">FAST FORCE · Dashboard</div>
          </div>
          <div className="flex items-center gap-3">
            {me && (
              <div className="text-sm text-muted-foreground">
                {me.email} · <span className="uppercase">{me.role}</span>
              </div>
            )}
            <Button variant="outline" onClick={logout} data-testid={AUTH.logoutBtn}>Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {error && (
          <div className="text-sm text-destructive border border-destructive/40 rounded p-3">{error}</div>
        )}

        {/* KPIs */}
        {summary && (
          <section className="grid gap-4 grid-cols-2 md:grid-cols-4" data-testid="admin-dashboard-kpis">
            <KpiCard
              label="Orders today"
              value={summary.today.orders}
              hint={formatMoney({ amount_minor: summary.today.revenue_minor, currency: summary.currency })}
              testId="kpi-orders-today"
            />
            <KpiCard
              label="Revenue (7 days)"
              value={formatMoney({ amount_minor: summary.week.revenue_minor, currency: summary.currency })}
              hint={`${summary.week.orders} orders`}
              testId="kpi-revenue-week"
            />
            <KpiCard
              label="Pending verification"
              value={summary.pending_verification}
              hint="new orders waiting"
              highlight={summary.pending_verification > 0}
              testId="kpi-pending"
            />
            <KpiCard
              label="Ready for delivery"
              value={summary.ready_for_delivery}
              hint="assign a driver"
              testId="kpi-ready"
            />
          </section>
        )}

        {/* Shortcuts */}
        <section className="grid gap-4 md:grid-cols-3">
          <ShortcutCard to="/admin/orders" title="Orders" desc="Verify, assign delivery, track fulfilment" testId="shortcut-orders" />
          <ShortcutCard to="/admin/products" title="Products" desc="Catalogue, stock, bulk import" testId="shortcut-products" />
          <ShortcutCard to="/admin/customers" title="Customers" desc="Directory & order history" testId="shortcut-customers" />
          <ShortcutCard to="/admin/inquiries" title="Inquiries" desc="WhatsApp inquiry log from PDPs" testId="shortcut-inquiries" />
          <ShortcutCard to="/admin/contacts" title="Contact details" desc="Salesmen roster & homepage WhatsApp" testId="shortcut-contacts" />
          <ShortcutCard to="/admin/invoices" title="Invoices" desc="UAE VAT tax invoices & PDFs" testId="shortcut-invoices" />
          <ShortcutCard to="/admin/drivers" title="Drivers" desc="Delivery personnel" testId="shortcut-drivers" />
          <ShortcutCard to="/admin/settings" title="Settings" desc="Company, invoice, delivery fees" testId="shortcut-settings" />
        </section>

        {/* Recent orders + low stock */}
        <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base">Recent orders</CardTitle>
                <CardDescription>Latest 5 placed</CardDescription>
              </div>
              <Button asChild size="sm" variant="ghost"><Link to="/admin/orders">All orders →</Link></Button>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {summary?.recent_orders?.map((o) => (
                  <Link
                    key={o.id}
                    to={`/admin/orders/${o.id}`}
                    className="flex items-center gap-4 py-3 text-sm hover:bg-muted/40 px-2 -mx-2 rounded"
                    data-testid={`recent-order-${o.order_number}`}
                  >
                    <div className="font-mono text-xs w-32 truncate">{o.order_number}</div>
                    <div className="flex-1 truncate">{o.customer_name}</div>
                    <Badge variant="secondary" className="text-[10px]">{o.status}</Badge>
                    <div className="text-right w-24 font-medium">
                      {formatMoney({ amount_minor: o.grand_total_minor, currency: o.currency })}
                    </div>
                  </Link>
                ))}
                {(!summary?.recent_orders || summary.recent_orders.length === 0) && (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    No orders yet — place a test order from the <Link to="/catalog" className="underline">catalogue</Link>.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Not available</CardTitle>
              <CardDescription>{summary?.not_available?.length || 0} products marked unavailable</CardDescription>
            </CardHeader>
            <CardContent>
              {summary?.not_available?.length ? (
                <div className="space-y-2 text-sm max-h-72 overflow-auto pr-2">
                  {summary.not_available.map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-2" data-testid={`not-available-${p.sku}`}>
                      <div className="min-w-0">
                        <div className="truncate">{p.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{p.sku}</div>
                      </div>
                      <Link to={`/admin/products/${p.id}`} className="text-xs text-red-600 hover:underline">
                        Edit
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">All active products are marked available.</div>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Danger Zone — permanently wipes all orders/invoices/carts/inquiries for a clean production reset. */}
        <section className="border border-red-300 rounded-lg bg-red-50/50 p-5 space-y-3" data-testid="admin-danger-zone">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="text-sm font-semibold text-red-800">Danger zone · Reset business data</div>
              <div className="text-xs text-red-700/80 mt-1">
                Permanently deletes every order, invoice, cart and inquiry for this workspace. Products, customers, admins, drivers and settings are kept. Use this once after redeploy to clear test data.
              </div>
            </div>
            {!wipeOpen && !wipeResult && (
              <Button
                variant="destructive"
                onClick={() => { setWipeOpen(true); setWipeConfirm(""); setWipeErr(""); }}
                data-testid="admin-danger-open"
              >
                Reset business data…
              </Button>
            )}
          </div>

          {wipeOpen && !wipeResult && (
            <div className="border border-red-400 bg-white rounded p-4 space-y-3">
              <div className="text-sm text-red-900">
                This cannot be undone. Type <code className="font-mono font-semibold">WIPE</code> to confirm.
              </div>
              <input
                type="text"
                value={wipeConfirm}
                onChange={(e) => setWipeConfirm(e.target.value)}
                placeholder="Type WIPE"
                className="w-full sm:w-64 border border-red-400 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="admin-danger-confirm-input"
              />
              {wipeErr && <div className="text-sm text-red-700">{wipeErr}</div>}
              <div className="flex gap-2">
                <Button
                  variant="destructive"
                  disabled={wipeConfirm !== "WIPE" || wipeBusy}
                  onClick={runWipe}
                  data-testid="admin-danger-run"
                >
                  {wipeBusy ? "Wiping…" : "Yes, wipe all business data"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setWipeOpen(false); setWipeConfirm(""); setWipeErr(""); }}
                  disabled={wipeBusy}
                  data-testid="admin-danger-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {wipeResult && (
            <div className="border border-green-400 bg-white rounded p-4 space-y-2" data-testid="admin-danger-result">
              <div className="text-sm font-semibold text-green-800">Business data cleared.</div>
              <div className="text-xs text-slate-700 grid grid-cols-2 sm:grid-cols-5 gap-2">
                {Object.entries(wipeResult).map(([k, v]) => (
                  <div key={k} className="border rounded px-2 py-1">
                    <div className="text-[10px] uppercase text-slate-500">{k.replace(/_/g, " ")}</div>
                    <div className="text-lg font-semibold tabular-nums">{v}</div>
                  </div>
                ))}
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setWipeResult(null); setWipeOpen(false); }} data-testid="admin-danger-dismiss">
                Dismiss
              </Button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function KpiCard({ label, value, hint, highlight, testId }) {
  return (
    <Card className={highlight ? "border-amber-400" : ""} data-testid={testId}>
      <CardContent className="pt-6 space-y-1">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function ShortcutCard({ to, title, desc, testId }) {
  return (
    <Link to={to} className="block group" data-testid={testId}>
      <Card className="hover:border-slate-900 transition-colors h-full">
        <CardContent className="pt-6">
          <div className="text-base font-semibold group-hover:underline">{title}</div>
          <div className="text-sm text-muted-foreground mt-1">{desc}</div>
        </CardContent>
      </Card>
    </Link>
  );
}
